import * as React from "react";

import {
  Box,
  FormHelperText,
  makeStyles,
  Theme,
  Typography,
} from "@material-ui/core";
import { useMutation, useQuery } from "react-query";
import * as Redux from "react-redux";

import { MopButton } from "client/components";
import {
  UIFieldMap,
  useMerchantModel,
  useRestore,
  useTranslation,
  useUiFieldMap,
} from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { MerchantFieldMapper } from "client/lib/apt-merchant";
import { UserService } from "client/services";
import {
  errorActions,
  formCompletionActions,
  RootState,
  userActions,
} from "client/store";

import { FormBuilder } from "../FormBuilder";
import { MerchantAgreementForm } from "../MerchantAgreementForm";

const useStyles = makeStyles((theme: Theme) => ({
  heading: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: `${24 / 16}rem`,
  },
  subHeading: {
    fontSize: `${14 / 16}rem`,
  },
  footer: {
    fontSize: `${12 / 16}rem`,
  },
}));

export type CashFormFieldUiMap = MerchantFieldMapper<UIFieldMap>;

const INVALID_FEES_ERROR = "INVALID_FEE";
const INVALID_FEES_MESSAGE =
  "We've encountered an issue with your application. Please contact your Account Manager or reach out to our Merchant Support team to resolve this. na-afterpay-merchant-admin@squareup.com";

export const CashPayFieldCustomHeader: React.FC<{
  fieldNameResourceKey: string;
}> = ({ fieldNameResourceKey }) => {
  const t = useTranslation();

  return (
    <Box>
      <Typography variant="body2">
        <strong>{t(fieldNameResourceKey)}</strong>
      </Typography>
    </Box>
  );
};
// field order reflects input order
export const uiFieldMap: CashFormFieldUiMap = {
  cashPay: {
    cashPayLogoUrl: {
      width: 12,
      customFieldHeader: (
        <CashPayFieldCustomHeader
          fieldNameResourceKey={"cashPayLogoUrlFieldHeader"}
        />
      ),
    },
  },
};

export const CashPayForm: React.FC = () => {
  const [isAgreementAccepted, setIsAgreementAccepted] = React.useState(false);
  const [error, setError] = React.useState("");

  const [showFooter, setShowFooter] = React.useState(false);
  const {
    merchant,
    formatMerchantForCashPayInfo,
    validateMerchant,
  } = useMerchantModel();
  const t = useTranslation();
  const classes = useStyles();
  const dispatch = Redux.useDispatch();
  useRestore();

  const { orderedUiFieldMap } = useUiFieldMap(uiFieldMap);
  const {
    formCompletion,
    merchant: {
      merchantAccount: { merchantId, mlsProspectRef },
    },
  } = Redux.useSelector(({ formCompletion, merchant }: RootState) => ({
    formCompletion,
    merchant,
  }));

  React.useEffect(() => {
    const cashPayAgreementAccepted = formCompletion.cashPayAgreementAccepted;
    if (cashPayAgreementAccepted !== undefined) {
      setIsAgreementAccepted(formCompletion.cashPayAgreementAccepted);
    }
  }, [formCompletion.cashPayAgreementAccepted]);

  const { mutate: submitCashPayInfo } = useMutation(
    UserService.updateCashPayInfo,
    {
      onError: (err: Error) => {
        dispatch(errorActions.setError(err.message));
      },
      onSuccess: ({
        validation_errors,
        formCompletedState,
        userState,
        error,
      }) => {
        if (validation_errors) {
          dispatch(errorActions.setFieldErrorMap(validation_errors));
        } else if (error) {
          dispatch(errorActions.setError(error));
        } else {
          if (userState) {
            dispatch(userActions.setUserState(userState));
          }
          if (userState === "MAINTENANCE_MODE") {
            return;
          }
          if (formCompletedState) {
            dispatch(
              formCompletionActions.setFormCompletion(formCompletedState)
            );
          }
          logAmplitudeEvent("CASH_PAY_FORM", "cash_pay_agreement_accepted", {
            merchantId: merchantId ?? "",
          });
          logAmplitudeEvent("CASH_PAY_FORM", "cash_pay_form_submitted", {
            merchantId: merchantId ?? "",
          });
        }
      },
    }
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAgreementAccepted) {
      dispatch(
        errorActions.setError(
          "Please accept merchant agreement and privacy policy before submitting."
        )
      );
      return;
    }
    if (error) {
      dispatch(errorActions.setError(error));
      return;
    }
    const isValid = validateMerchant(merchant.cashPay);
    if (isValid) {
      const data = formatMerchantForCashPayInfo();
      submitCashPayInfo(data);
    }
  };

  // If merchant changes display name value from pre-filled store name
  // or uploads a logo, show footer informing merchant their profile will
  // go through a review process
  React.useEffect(() => {
    const logoUrlValue = merchant.cashPay.cashPayLogoUrl.value;

    setShowFooter(Boolean(logoUrlValue));

    if (logoUrlValue) {
      logAmplitudeEvent("CASH_PAY_FORM", "cash_pay_logo_uploaded", {
        merchantId: merchantId ?? "",
      });
    }
  }, [merchant.cashPay.cashPayLogoUrl.value]);

  React.useEffect(() => {
    if (merchantId) {
      // Grab source from URL if available.
      // Links with no source specified are defaulted to be 'email' based
      const urlSearchString = window.location.search;
      const params = new URLSearchParams(urlSearchString);
      const source = params.get("source");

      logAmplitudeEvent("CASH_PAY_FORM", "cash_pay_form_loaded", {
        merchantId: merchantId,
        source: source ?? "email",
      });
    }
  }, [merchantId]);

  useQuery<{ success?: boolean; error?: string }, Error>(
    ["update-cashpay-channel", mlsProspectRef],
    () => UserService.updateCashpayChannel(mlsProspectRef),
    {
      onError(err) {
        dispatch(errorActions.setError(err.message));
      },
      onSuccess({ error }) {
        if (error == INVALID_FEES_ERROR) {
          setError(INVALID_FEES_MESSAGE);
        }
      },
      enabled: Boolean(mlsProspectRef),
    }
  );

  return (
    <Box
      padding={10}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      data-testid="cash-pay-form"
    >
      <Box mb={3}>
        <Box mb={1}>
          <Typography variant="h5" className={classes.heading}>
            {t("uiCashFormHeading")}
          </Typography>
        </Box>
        <Typography variant="body2" className={classes.subHeading}>
          {t("uiCashFormSubHeader")}
        </Typography>
      </Box>
      <Box alignItems="center">
        <FormBuilder
          fieldSet={merchant.cashPay}
          onSubmit={handleSubmit}
          uiFieldMap={orderedUiFieldMap.cashPay}
          customSubmitComponent={
            <>
              {error && (
                <Box margin="auto">
                  {error && (
                    <FormHelperText error={true}>
                      <strong>{error}</strong>
                    </FormHelperText>
                  )}
                </Box>
              )}
              <MopButton mopVariant="secondary" type="submit" fullWidth>
                Continue
              </MopButton>
            </>
          }
          footer={
            <Box>
              {showFooter && (
                <Box mt={4}>
                  <Typography
                    variant="body2"
                    align="center"
                    className={classes.footer}
                  >
                    <strong>{t("uiCashFormFooterCopy")}</strong>
                  </Typography>
                </Box>
              )}
              <Box mt={4} mb={4}>
                <MerchantAgreementForm
                  isAgreementAccepted={isAgreementAccepted}
                  onChange={(_, checked) => {
                    setIsAgreementAccepted(checked);
                    if (checked) {
                      logAmplitudeEvent(
                        "CASH_PAY_FORM",
                        "cash_pay_agreement_clicked",
                        {
                          merchantId: merchantId ?? "",
                        }
                      );
                    }
                  }}
                  disableCheckbox={formCompletion.cashPayAgreementAccepted}
                  product={"Cash"}
                />
              </Box>
            </Box>
          }
        />
      </Box>
    </Box>
  );
};
